/**
 * Authentication Store using Zustand
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { api } from '../lib/api';
import type { AuthUser, UserRole, LoginCredentials } from '../types';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  activeRole: UserRole | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  switchRole: (role: UserRole) => void;
  getActiveRole: () => UserRole | null;

  // Role checks
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  canAccess: (resource: string, action: string) => boolean;
}

// Permission matrix for role-based access
const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  SUPER_ADMIN: ['*:*'],
  ADMIN: [
    'user:*',
    'vehicle:*',
    'customer:*',
    'workflow:*',
    'task:*',
    'report:*',
    'settings:*',
    'yard:*',
  ],
  DISPATCHER: [
    'vehicle:read',
    'vehicle:update',
    'task:*',
    'workflow:read',
    'customer:read',
    'yard:read',
    'yard:move',
  ],
  YARD_WORKER: ['vehicle:read', 'task:read', 'task:update', 'yard:read'],
  WORKSHOP: ['vehicle:read', 'task:read', 'task:update', 'repair:*'],
  CLEANING: ['vehicle:read', 'task:read', 'task:update', 'cleaning:*'],
  QC_INSPECTOR: ['vehicle:read', 'task:read', 'task:update', 'qc:*'],
  CUSTOMER_ADMIN: ['vehicle:read', 'report:read', 'invoice:read'],
  CUSTOMER_USER: ['vehicle:read', 'report:read'],
  CUSTOMER_READONLY: ['vehicle:read'],
  INSURANCE_USER: ['vehicle:read', 'damage:read', 'appraisal:read'],
  APPRAISER: ['vehicle:read', 'damage:read', 'appraisal:*'],
  SUPPLIER: ['parts:read', 'parts:create'],
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      activeRole: null,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<{
            accessToken: string;
            refreshToken: string;
            user: AuthUser;
          }>('/auth/login', credentials);

          const responseData = response.data;

          // Validate response has required fields
          if (!responseData?.accessToken || !responseData?.refreshToken || !responseData?.user) {
            throw new Error('Ungültige Serverantwort');
          }

          const { accessToken, refreshToken, user: userData } = responseData;

          // Ensure user has all required fields (API might not include isActive)
          // Construct roles array from role + additionalRoles
          const additionalRoles =
            (userData as { additionalRoles?: UserRole[] }).additionalRoles || [];
          const allRoles = userData.role ? [userData.role, ...additionalRoles] : additionalRoles;

          const user: AuthUser = {
            ...userData,
            isActive: userData.isActive ?? true,
            roles: userData.roles || allRoles,
          };

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Anmeldung fehlgeschlagen';
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        set({
          user: null,
          isAuthenticated: false,
          error: null,
          activeRole: null,
        });
      },

      refreshUser: async () => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          set({ user: null, isAuthenticated: false });
          return;
        }

        try {
          const response = await api.get<AuthUser>('/auth/me');
          const userData = response.data;

          // Ensure user has all required fields
          // Construct roles array from role + additionalRoles
          const additionalRoles =
            (userData as { additionalRoles?: UserRole[] }).additionalRoles || [];
          const allRoles = userData.role ? [userData.role, ...additionalRoles] : additionalRoles;

          const user: AuthUser = {
            ...userData,
            isActive: userData.isActive ?? true,
            roles: userData.roles || allRoles,
          };

          set({
            user,
            isAuthenticated: true,
          });
        } catch {
          // Clear auth state on failure
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          set({
            user: null,
            isAuthenticated: false,
          });
        }
      },

      clearError: () => set({ error: null }),

      switchRole: (role: UserRole) => {
        const { user } = get();
        if (!user || !user.roles) return;

        // Validate that the user has this role
        if (!user.roles.includes(role)) return;

        set({ activeRole: role });
      },

      getActiveRole: () => {
        const { user, activeRole } = get();
        if (!user) return null;

        // If activeRole is set and user still has it, use it
        if (activeRole && user.roles?.includes(activeRole)) {
          return activeRole;
        }

        // Fall back to primary role
        return user.role || null;
      },

      hasRole: (role: UserRole) => {
        const { user } = get();
        if (!user || !user.roles) return false;
        return user.roles.includes(role);
      },

      hasAnyRole: (roles: UserRole[]) => {
        const { user } = get();
        if (!user || !user.roles) return false;
        return roles.some((role) => user.roles.includes(role));
      },

      isAdmin: () => {
        const { hasAnyRole } = get();
        return hasAnyRole(['SUPER_ADMIN', 'ADMIN']);
      },

      isSuperAdmin: () => {
        const { hasRole } = get();
        return hasRole('SUPER_ADMIN');
      },

      canAccess: (resource: string, action: string) => {
        const { user } = get();
        if (!user || !user.roles) return false;

        const userPermissions = user.roles.flatMap((role) => ROLE_PERMISSIONS[role] || []);

        // Check for wildcard permissions
        if (userPermissions.includes('*:*')) return true;
        if (userPermissions.includes(`${resource}:*`)) return true;
        if (userPermissions.includes(`*:${action}`)) return true;

        return userPermissions.includes(`${resource}:${action}`);
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        activeRole: state.activeRole,
      }),
    },
  ),
);
